import React, { useEffect, useState } from 'react'
import { Gwiazdki } from './KartaWykonawcy'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Battery50Icon, BoltIcon, CalendarDaysIcon, ChevronRightIcon, CpuChipIcon, HomeIcon, InboxStackIcon, PencilSquareIcon, PhotoIcon,
} from '@heroicons/react/24/outline'
import * as api from '../../api/rynek'
import { adresZApiKonta, getWizytowka } from '../../api/account'
import { czasDoTerminu, kodOgloszenia, zl } from '../../lib/rynek'
import { BEZ_UPRAWNIENIA_RYNKU } from '../../config/rynek'
import OcenaCeny from './OcenaCeny'
import ProfilNaRynku from './ProfilNaRynku'
import { Rozeta, odmiana } from './KartaWykonawcy'

/**
 * „Rynek ofert” dla konta firmowego (`/rynek`, decyzja właściciela 2026-10-21): ogłoszenia, które właściciel
 * VoltValue pokazał tej firmie. Bez kontaktu do Kowalskiego, dopóki ten nie wybierze firmy albo nie udostępni
 * kontaktu. Szczegóły i odpowiedź: `/rynek/:id` (`pages/RynekOgloszenieFirmy.jsx`).
 */

export const terminKrotko = (iso) => new Date(iso).toLocaleString('pl-PL', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })

/**
 * Stan udziału w zleceniu (wariant D, pakiet P0: `o.udzial.stan` z backendu `rynek/przetarg.stan_udzialu`): oferta
 * w 48 h jest potwierdzeniem udziału, potem wynik doboru i wybór klienta. Null poza tym trybem. Bez słów „przetarg”,
 * „konkurs”, „finaliści” (decyzja właściciela 2026-10-25).
 */
const stanUdzialu = (o) => {
  const u = o.udzial
  if (!u) return null
  switch (u.stan) {
    case 'zaproszona': {
      const czas = u.termin ? czasDoTerminu(u.termin) : null
      return czas
        ? { nazwa: `Złóż ofertę do ${terminKrotko(u.termin)}, ${czas.tekst}`, kolor: czas.kolor }
        : { nazwa: 'Złóż ofertę', kolor: 'bg-amber-400' }
    }
    case 'oferta_zlozona': return { nazwa: 'Oferta złożona', kolor: 'bg-blue-600' }
    case 'wygasla': return { nazwa: 'Termin na ofertę minął', kolor: 'bg-gray-400' }
    case 'odmowila': return { nazwa: 'Odmówiono udziału', kolor: 'bg-gray-400' }
    case 'finalista': return { nazwa: 'Twoja oferta przeszła dalej', kolor: 'bg-emerald-500' }
    case 'nie_przeszla': return { nazwa: 'Oferta nie przeszła dalej', kolor: 'bg-gray-400' }
    case 'wybrana': return { nazwa: 'Klient wybrał Twoją firmę', kolor: 'bg-emerald-600' }
    case 'niewybrana': return { nazwa: 'Klient wybrał innych wykonawców', kolor: 'bg-gray-400' }
    case 'zamkniete': return { nazwa: 'Zlecenie zamknięte', kolor: 'bg-gray-400' }
    default: return null
  }
}

/** Odświeża widok co minutę, żeby odliczanie do terminu nie stało w otwartej karcie. */
export const useCoMinute = () => {
  const [, ustaw] = useState(0)
  useEffect(() => {
    const zegar = setInterval(() => ustaw((x) => x + 1), 60000)
    return () => clearInterval(zegar)
  }, [])
}

/** Stan ogłoszenia z punktu widzenia firmy: krótki napis i kolor znacznika. */
export const stanDlaFirmy = (o) => {
  const odp = o.moja_odpowiedz
  if (o.koniec_kontaktu) return { nazwa: 'Klient prosi o koniec kontaktu', kolor: 'bg-gray-400' }
  const przetarg = stanUdzialu(o)
  if (przetarg) return przetarg
  if (o.wybrano_nas) return { nazwa: 'Klient wybrał Twoją ofertę', kolor: 'bg-emerald-600' }
  if (o.kontakt && o.tryb_kontaktu === 'dopasowanie' && !odp) return { nazwa: 'Nowy klient: zadzwoń', kolor: 'bg-emerald-500' }
  if (o.kontakt && o.tryb_kontaktu !== 'dopasowanie') return { nazwa: 'Klient udostępnił kontakt', kolor: 'bg-emerald-500' }
  if (odp?.status === 'odrzucona') return { nazwa: 'Klient odrzucił ofertę', kolor: 'bg-gray-400' }
  if (!o.otwarte) return { nazwa: 'Zamknięte', kolor: 'bg-gray-400' }
  if (odp?.status === 'wyslana') return { nazwa: 'Oferta wysłana', kolor: 'bg-blue-600' }
  if (odp?.status === 'wycofana') return { nazwa: 'Oferta wycofana', kolor: 'bg-gray-400' }
  return { nazwa: 'Czeka na Twoją ofertę', kolor: 'bg-amber-400' }
}

const ZLOZONE = ['oferta_zlozona', 'finalista', 'nie_przeszla', 'wybrana', 'niewybrana']
const DALEJ = ['finalista', 'wybrana', 'niewybrana']

const stanOferty = (o) => o.udzial?.stan
const wybrana = (o) => stanOferty(o) === 'wybrana' || Boolean(o.wybrano_nas)

/**
 * Podsumowanie u góry „Rynku ofert” (uwagi przeglądu E1) i zarazem historia zleceń (prośba właściciela 2026-10-30):
 * klik w liczbę pokazuje te zlecenia (`/rynek?filtr=…`). Wybór klienta poza konkursem też liczy się jako złożona oferta,
 * która przeszła dalej, bo inaczej liczby sobie przeczyły („0 złożonych, 1 wybrana”).
 */
export const FILTRY_RYNKU = [
  { klucz: 'zaproszenia', nazwa: 'Zaproszenia', pasuje: () => true },
  { klucz: 'oferty', nazwa: 'Złożone oferty',
    pasuje: (o) => ZLOZONE.includes(stanOferty(o)) || (Boolean(o.moja_odpowiedz) && o.moja_odpowiedz.status !== 'wycofana') },
  { klucz: 'dalej', nazwa: 'Przeszły dalej', pasuje: (o) => DALEJ.includes(stanOferty(o)) || wybrana(o) },
  { klucz: 'wybrane', nazwa: 'Wybrane przez klienta', pasuje: wybrana },
]

export const podsumowanieRynku = (lista) => Object.fromEntries(
  FILTRY_RYNKU.map((f) => [f.klucz, (lista || []).filter(f.pasuje).length]),
)

const Podsumowanie = ({ lista, filtr }) => {
  const p = podsumowanieRynku(lista)
  return (
    <nav aria-label="Podsumowanie" className="mt-6 grid grid-cols-2 overflow-hidden rounded-[18px] border border-gray-200 bg-white sm:grid-cols-4">
      {FILTRY_RYNKU.map(({ klucz, nazwa }, i) => {
        const n = p[klucz]
        const aktywny = filtr?.klucz === klucz
        return (
          <Link key={klucz} to={aktywny ? '/rynek' : `/rynek?filtr=${klucz}`} aria-current={aktywny ? 'true' : undefined}
                className={`flex flex-col gap-0.5 px-4 py-3.5 no-underline transition-colors hover:bg-gray-50 sm:px-5 ${aktywny ? 'shadow-[inset_0_-3px_0_0_#2563eb]' : ''}
                  ${i % 2 ? 'border-l border-gray-100' : ''} ${i > 1 ? 'border-t border-gray-100 sm:border-t-0' : ''} ${i === 2 ? 'sm:border-l' : ''}`}>
            <span className={`text-[26px] font-bold leading-tight tabular-nums ${klucz === 'wybrane' && n > 0 ? 'text-emerald-700' : 'text-gray-900'}`}>{n}</span>
            <span className={`text-[13px] ${aktywny ? 'font-semibold text-blue-700' : 'text-gray-500'}`}>{nazwa}</span>
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * Jeden kafelek „Wizytówka” (uwagi przeglądu F4, 2026-10-25): jak widzi Was klient przy ofercie (logo, nazwa, znaczek
 * certyfikatu, zdjęcia, ocena) i profil na Rynku z ankiety (obszar, zakres, marki) z jej edycją w tym samym miejscu.
 */
const KafelekWizytowki = ({ onZapisano }) => {
  const [w, setW] = useState(null)
  useEffect(() => { getWizytowka().then((r) => { if (r.ok) setW(r.data) }) }, [])
  const nazwa = w?.firma?.nazwa || ''
  return (
    <section aria-labelledby="wizytowka-rynku" className="mt-6 rounded-[18px] border border-gray-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        {w && (
          <span className="flex h-[42px] w-[72px] flex-none items-center justify-center overflow-hidden rounded-lg border border-gray-200 p-1" style={{ background: '#FFFFFF' }}>
            {w.logo_url
              ? <img src={adresZApiKonta(w.logo_url)} alt={`Logo ${nazwa}`} className="max-h-full max-w-full object-contain" />
              : <span aria-hidden="true" className="text-base font-bold text-blue-700">{nazwa.trim().charAt(0).toUpperCase()}</span>}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h2 id="wizytowka-rynku" className="m-0 flex items-center gap-1.5 text-base font-bold text-gray-900">
            Wizytówka{w?.certyfikat && <Rozeta className="h-4 w-4" />}
          </h2>
          {w && (
            <p className="m-0 flex flex-wrap gap-x-3 gap-y-0.5 text-[13px] text-gray-600">
              <span>Tak widzi Was klient przy ofercie</span>
              <span className="inline-flex items-center gap-1"><PhotoIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                {w.liczba_zdjec || 0} {odmiana(w.liczba_zdjec || 0, 'zdjęcie', 'zdjęcia', 'zdjęć')}</span>
              {w.google?.ocena != null && <span className="inline-flex items-center gap-1"><Gwiazdki ocena={w.google.ocena} /> {String(w.google.ocena).replace('.', ',')} w Google</span>}
            </p>
          )}
        </div>
        <Link to="/firma/ankieta#wizytowka"
              className="inline-flex h-[40px] items-center gap-1.5 rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 text-sm font-semibold text-gray-800 no-underline hover:border-blue-500 hover:text-blue-700">
          <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />Edytuj wizytówkę
        </Link>
      </div>
      <div className="mt-4 border-t border-gray-100 pt-4">
        <ProfilNaRynku onZapisano={onZapisano} wKafelku />
      </div>
    </section>
  )
}

const data = (iso) => new Date(iso).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })

/**
 * Cena na karcie zlecenia (prośba właściciela 2026-10-30): najpierw oferta tej firmy, bo to jej liczba do porównań,
 * potem to, co wiemy o oczekiwaniach klienta. Cena posiadanej oferty przychodzi tylko za zgodą klienta.
 */
export const cenaNaLiscie = (o) => {
  const moja = o.moja_odpowiedz?.status !== 'wycofana' ? o.moja_odpowiedz?.cena_brutto : null
  if (moja) return { kwota: zl(moja), opis: 'Twoja oferta' }
  if (o.zakres_od && o.zakres_do) return { kwota: `${Math.round(o.zakres_od).toLocaleString('pl-PL')} do ${zl(o.zakres_do)}`, opis: 'Zakres ceny', zakres: true }
  if (o.budzet_brutto) return { kwota: zl(o.budzet_brutto), opis: 'Budżet klienta' }
  if (o.cena_oferty) return { kwota: zl(o.cena_oferty), opis: 'Oferta, którą klient ma' }
  return null
}

const KartaZlecenia = ({ o }) => {
  const s = stanDlaFirmy(o)
  const cena = cenaNaLiscie(o)
  const opis = o.opis || {}
  const fakty = [
    opis.kwp && [BoltIcon, `${String(opis.kwp).replace('.', ',')} kWp`],
    opis.falownik_kw && [CpuChipIcon, `${String(opis.falownik_kw).replace('.', ',')} kW`],
    opis.magazyn_kwh && [Battery50Icon, `${String(opis.magazyn_kwh).replace('.', ',')} kWh`],
    opis.dach && [HomeIcon, opis.dach],
    o.termin_nazwa && [CalendarDaysIcon, o.termin_nazwa],
  ].filter(Boolean)
  return (
    <Link to={`/rynek/${kodOgloszenia(o)}`}
          className="group flex items-center gap-3 rounded-[18px] border border-gray-200 bg-white p-4 no-underline transition hover:border-blue-400 hover:shadow-sm sm:gap-4 sm:p-5">
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <p className="m-0 flex flex-wrap items-center gap-2 text-base font-bold leading-snug text-gray-900">
            {o.typ_nazwa}
            {o.nowe && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">nowe</span>}
          </p>
          <p className="m-0 mt-0.5 text-[13px] text-gray-500">
            {[o.miejscowosc, o.powiat, o.wojewodztwo_nazwa].filter(Boolean).join(', ')}
            {o.pokazano_at && ` · pokazane ${data(o.pokazano_at)}`}
          </p>
          {fakty.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
              {fakty.map(([Ikona, t]) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-700">
                  <Ikona className="h-4 w-4 text-gray-400" aria-hidden="true" />{t}
                </span>
              ))}
            </div>
          )}
          <p className="m-0 mt-2.5 flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-gray-800">
            <span className={`h-2 w-2 rounded-full ${s.kolor}`} aria-hidden="true" />{s.nazwa}
            {o.klient_id && <span className="font-normal text-gray-500">· w Twoich klientach</span>}
          </p>
        </div>
        {cena && (
          <div className="flex-none sm:text-right">
            <p className="m-0 whitespace-nowrap text-lg font-bold leading-tight tabular-nums text-gray-900">{cena.kwota}</p>
            <p className="m-0 mt-0.5 text-[12.5px] text-gray-500">{cena.opis}</p>
            {cena.zakres && <OcenaCeny o={o} zakres={{ od: o.zakres_od, do: o.zakres_do }} className="mt-1 text-[12.5px] sm:justify-end" />}
          </div>
        )}
      </div>
      <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-300 group-hover:text-blue-600" aria-hidden="true" />
    </Link>
  )
}

const RynekFirmy = () => {
  useCoMinute()
  const [stan, setStan] = useState({ ladowanie: true, blad: '', kod: null, lista: [] })
  const [odswiez, setOdswiez] = useState(0)
  const [parametry] = useSearchParams()
  const filtr = FILTRY_RYNKU.find((f) => f.klucz === parametry.get('filtr')) || null
  const widoczne = filtr ? stan.lista.filter(filtr.pasuje) : stan.lista

  useEffect(() => {
    api.ogloszeniaFirmy().then((r) => setStan({
      ladowanie: false, blad: r.ok ? '' : r.error, kod: r.data?.kod || null, lista: r.ok ? r.data : [],
    }))
  }, [odswiez])

  return (
    <div className="bg-gray-50 text-gray-900">
      <div className="mx-auto max-w-4xl px-4 pb-24 pt-8 sm:px-6 sm:pt-12">
        <header className="flex items-start gap-4">
          <span aria-hidden="true" className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-[0_8px_18px_-8px_rgba(37,99,235,0.8)]">
            <InboxStackIcon className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="m-0 text-[28px] font-bold leading-tight tracking-[-0.02em] sm:text-[34px]">Rynek ofert</h1>
            <p className="m-0 mt-1 max-w-2xl text-[15px] leading-6 text-gray-600">
              Zlecenia klientów z Waszej okolicy. Złóż ofertę w 48 godzin, a kontakt dostaniesz, gdy klient wybierze Twoją firmę.
            </p>
          </div>
        </header>

        {!stan.ladowanie && !stan.blad && stan.lista.length > 0 && <Podsumowanie lista={stan.lista} filtr={filtr} />}

        {/* Podpowiedź „Rozszerz obszar działania” usunięta (decyzja właściciela 2026-10-25, uwagi przeglądu F5). */}
        {/* Historia (filtr ze statystyk): sama lista, wizytówka tylko w widoku głównym. */}
        {!filtr && <KafelekWizytowki onZapisano={() => setOdswiez((x) => x + 1)} />}

        <div className="mt-8">
          {stan.ladowanie && <p className="text-sm text-gray-500">Wczytuję…</p>}
          {!stan.ladowanie && stan.kod === 'bez_firmy' && (
            <p className="rounded-[18px] border border-gray-200 bg-white p-5 text-sm leading-6 text-gray-700">
              Rynek ofert jest dla kont z podpiętą firmą. Podaj NIP w <Link to="/konto?sekcja=firma" className="font-semibold text-blue-700">ustawieniach konta</Link>,
              a pokażemy Ci ogłoszenia klientów z Twojej okolicy.
            </p>
          )}
          {!stan.ladowanie && stan.kod === 'bez_uprawnienia' && (
            <p className="rounded-[18px] border border-gray-200 bg-white p-5 text-sm leading-6 text-gray-700">{BEZ_UPRAWNIENIA_RYNKU}</p>
          )}
          {!stan.ladowanie && stan.kod === 'brak_ankiety' && (
            <p className="rounded-[18px] border border-dashed border-gray-300 bg-white p-6 text-sm leading-6 text-gray-600">
              Zlecenia z Rynku zobaczysz tu po zapisaniu ankiety firmy.
            </p>
          )}
          {!stan.ladowanie && stan.blad && !['bez_firmy', 'bez_uprawnienia', 'brak_ankiety'].includes(stan.kod) && <p role="alert" className="text-sm text-red-700">{stan.blad}</p>}
          {!stan.ladowanie && !stan.blad && !stan.lista.length && (
            <p className="rounded-[18px] border border-dashed border-gray-300 bg-white p-6 text-sm leading-6 text-gray-600">
              Na razie nie ma ogłoszeń dla Twojej firmy. Gdy pokażemy Ci nowe ogłoszenie, dostaniesz e-mail i zobaczysz je tutaj.
            </p>
          )}
          {filtr && !stan.ladowanie && !stan.blad && (
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="m-0 text-lg font-bold text-gray-900">{filtr.nazwa}</h2>
              <Link to="/rynek" className="text-sm font-semibold text-blue-700 no-underline hover:underline">Wróć do Rynku ofert</Link>
            </div>
          )}
          {filtr && stan.lista.length > 0 && !widoczne.length && (
            <p className="rounded-[18px] border border-dashed border-gray-300 bg-white p-6 text-sm leading-6 text-gray-600">Nie ma tu jeszcze żadnego zlecenia.</p>
          )}
          {widoczne.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {widoczne.map((o) => <li key={o.id}><KartaZlecenia o={o} /></li>)}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default RynekFirmy
